import { useState, useEffect, useRef, useCallback } from "react";
import { X, Printer, Download, Loader2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure pdf.js worker — använd version som EXAKT matchar pdfjs-dist i node_modules
// (annars klagar pdf.js: "API version X does not match Worker version Y")
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Inline PDF-viewer-modal med fullständig multi-page rendering via react-pdf.
 * - Renderar alla sidor i en scrollbar lista
 * - Print-knapp som öppnar inbyggd print-dialog
 * - Download-knapp som tvingar nedladdning
 * - För icke-PDF (bilder) faller vi tillbaka till <img>
 */
export function PdfViewerModal({ url, title, mime, pages, startIndex = 0, onClose }) {
  // Multi-sida bildbläddring: `pages` = [{url,title,mime}, ...] → svep/pilar
  const multi = Array.isArray(pages) && pages.length > 1;
  const [pageIdx, setPageIdx] = useState(
    Math.min(startIndex || 0, (pages?.length || 1) - 1)
  );
  if (multi) {
    const cur = pages[pageIdx] || pages[0];
    url = cur.url;
    title = cur.title;
    mime = cur.mime;
  }
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [baseWidth, setBaseWidth] = useState(800);
  const [zoom, setZoom] = useState(1); // 0.5 – 3
  const containerRef = useRef(null);
  const pageRefs = useRef({});

  const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
  const zoomIn = () => {
    const i = ZOOM_STEPS.findIndex((z) => z >= zoom);
    setZoom(ZOOM_STEPS[Math.min(i + 1, ZOOM_STEPS.length - 1)]);
  };
  const zoomOut = () => {
    const i = ZOOM_STEPS.findIndex((z) => z >= zoom);
    setZoom(ZOOM_STEPS[Math.max(i - 1, 0)]);
  };
  const resetZoom = () => setZoom(1);

  // Bläddring (multi-bild): pilar, tangentbord och touch-svep
  const goRel = useCallback(
    (delta) => {
      if (!multi) return;
      setPageIdx((i) => {
        const next = Math.max(0, Math.min(pages.length - 1, i + delta));
        if (next !== i) setLoading(true);
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multi, pages?.length]
  );
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 60) goRel(dx < 0 ? 1 : -1);
  };

  // Esc + lock body scroll + +/- zoom shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || (e.key === "=" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        zoomIn();
      }
      if (e.key === "-" || (e.key === "_" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        zoomOut();
      }
      if (e.key === "0" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        resetZoom();
      }
      if (e.key === "ArrowRight") goRel(1);
      if (e.key === "ArrowLeft") goRel(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, zoom]);

  // Responsive page width — fit container (base 100% width)
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setBaseWidth(Math.min(containerRef.current.clientWidth - 24, 1100));
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const pageWidth = baseWidth * zoom;

  const isPdf =
    (mime || "").toLowerCase().includes("pdf") ||
    /\.pdf(\?|$)/i.test(title || "") ||
    /\.pdf(\?|$)/i.test(url || "");
  const isImage =
    (mime || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(title || "");

  // Build download URL — keep token; let backend default to inline disposition.
  const downloadUrl = url;
  void downloadUrl; // behåll variabeln i fall andra komponenter använder ref

  // Hämta PDF som blob — används för att bygga riktig File för Web Share API
  // (iOS) eller blob-URL för <a download> (desktop). Detta ÄR det enda sättet
  // att få iOS Safari att inte bara öppna PDF i ny flik.
  const fetchAsFile = useCallback(async () => {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const filename = (title || "fil").replace(/[\\/]/g, "_") + (
      /\.[a-z0-9]{2,5}$/i.test(title || "") ? "" : ".pdf"
    );
    const file = new File([blob], filename, { type: blob.type || "application/pdf" });
    return { file, blob, filename };
  }, [url, title]);

  // DOWNLOAD: iOS Safari ignorerar `<a download>`. Använd Web Share API om
  // tillgängligt (öppnar iOS Share Sheet med "Spara till Filer", AirDrop osv).
  // Desktop: skapa blob-URL + osynlig <a download> → spar fungerar direkt.
  const handleDownload = useCallback(async (e) => {
    e?.preventDefault?.();
    try {
      const { file, blob, filename } = await fetchAsFile();
      // 1) iOS / mobile: Web Share med fil → Share Sheet (Filer/Print/AirDrop)
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (err) {
          // Användaren avbröt eller delning misslyckades — falla tillbaka
          if (err && err.name === "AbortError") return;
        }
      }
      // 2) Desktop: blob-URL + programmatisk download-click
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        URL.revokeObjectURL(objUrl);
      }, 5000);
    } catch (err) {
      // 3) Sista utvägen — öppna i ny flik (browser sköter resten)
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [fetchAsFile, url]);

  // PRINT:
  // - Desktop: dold iframe → print() = native print-dialog utan att lämna sidan.
  // - iOS Safari: iframe.print() är opålitligt — använd Web Share Sheet istället
  //   så användaren kan välja "Skriv ut" från iOS-systemet (med AirPrint, etc).
  const printPdf = useCallback(async () => {
    // Detektera mobile/iOS — iframe.print() funkar dåligt där
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isMobile = isIOS || /Android|Mobile/i.test(ua);

    if (isMobile && navigator.canShare) {
      // På mobil: prova Web Share → iOS Share Sheet har "Skriv ut" + AirPrint
      try {
        const { file, filename } = await fetchAsFile();
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
          return;
        }
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }

    // Desktop / fallback: dold iframe + .print()
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    };
    document.body.appendChild(iframe);
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch { /* noop */ }
    }, 60_000);
  }, [url, fetchAsFile]);

  const onDocLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
    setLoading(false);
  };
  const onDocLoadError = (err) => {
    setLoadError(err?.message || "Kunde inte ladda filen");
    setLoading(false);
  };

  // Memoize the file prop so react-pdf doesn't refetch on every render
  const [fileProp] = useState(() => ({ url, withCredentials: false }));

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      data-testid="pdf-viewer-backdrop"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white/95 border-b border-[color:var(--border)] sticky top-0"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-medium truncate flex-1" title={title}>
          {title || "Förhandsvisning"}
          {numPages ? (
            <span className="text-[color:var(--text-tertiary)] ml-2 text-xs font-normal">
              · {numPages} sidor
            </span>
          ) : null}
        </h2>
        <div className="flex items-center gap-1 shrink-0">
          {isPdf && numPages && (
            <div
              className="flex items-center gap-0.5 mr-1 bg-[#F5F7F4] rounded-md px-0.5"
              data-testid="pdf-zoom-controls"
            >
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= ZOOM_STEPS[0]}
                className="p-1.5 rounded hover:bg-white text-[color:var(--text-secondary)] disabled:opacity-30"
                title="Zooma ut"
                data-testid="pdf-zoom-out"
                aria-label="Zooma ut"
              >
                <ZoomOut size={14} />
              </button>
              <button
                type="button"
                onClick={resetZoom}
                className="text-xs font-medium min-w-[40px] px-1 py-1 rounded hover:bg-white text-[color:var(--text-secondary)] tabular-nums"
                title="Återställ zoom"
                data-testid="pdf-zoom-reset"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                className="p-1.5 rounded hover:bg-white text-[color:var(--text-secondary)] disabled:opacity-30"
                title="Zooma in"
                data-testid="pdf-zoom-in"
                aria-label="Zooma in"
              >
                <ZoomIn size={14} />
              </button>
            </div>
          )}
          {isPdf && (
            <button
              type="button"
              onClick={printPdf}
              className="p-2 rounded-md hover:bg-[#F5F7F4] text-[color:var(--text-secondary)]"
              title="Skriv ut"
              data-testid="pdf-print"
              aria-label="Skriv ut"
            >
              <Printer size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            className="p-2 rounded-md hover:bg-[#F5F7F4] text-[color:var(--text-secondary)]"
            title="Ladda ner / Dela"
            data-testid="pdf-download"
            aria-label="Ladda ner"
          >
            <Download size={16} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-[#F5F7F4] text-[color:var(--text-secondary)]"
            data-testid="pdf-viewer-close"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-auto bg-[#F5F7F4] px-3 py-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={multi ? onTouchStart : undefined}
        onTouchEnd={multi ? onTouchEnd : undefined}
        data-testid="pdf-viewer-body"
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-[color:var(--text-secondary)] bg-white/90 px-3 py-2 rounded-lg shadow-sm">
              <Loader2 className="animate-spin" size={16} />
              <span className="text-sm">Laddar…</span>
            </div>
          </div>
        )}
        {loadError && (
          <div className="flex flex-col items-center justify-center h-full text-center text-[color:var(--text-secondary)] gap-2">
            <p className="text-sm">{loadError}</p>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-md border border-[color:var(--moss)] text-[color:var(--moss)] text-sm"
            >
              Försök öppna i ny flik
            </a>
          </div>
        )}
        {isImage ? (
          <img
            src={url}
            alt={title}
            onLoad={() => setLoading(false)}
            className="max-w-full mx-auto block rounded-sm shadow-sm"
            data-testid="pdf-viewer-image"
          />
        ) : isPdf ? (
          <Document
            file={fileProp}
            onLoadSuccess={onDocLoadSuccess}
            onLoadError={onDocLoadError}
            loading=""
            error=""
            className="flex flex-col items-center gap-3"
          >
            {numPages &&
              Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                <div
                  key={p}
                  ref={(el) => (pageRefs.current[p] = el)}
                  className="shadow-md bg-white"
                  data-testid={`pdf-page-${p}`}
                >
                  <Page
                    pageNumber={p}
                    width={pageWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              ))}
          </Document>
        ) : !loading ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <p className="text-sm text-[color:var(--text-secondary)] mb-3">
              Denna filtyp kan inte förhandsvisas direkt i appen.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              download={title}
              className="px-4 py-2 rounded-md bg-[color:var(--moss)] text-white text-sm flex items-center gap-1.5"
            >
              <Download size={14} /> Ladda ner {title}
            </a>
          </div>
        ) : null}
        {multi && (
          <>
            <div
              className="fixed bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/70 text-white rounded-full px-3 py-1.5 shadow-lg z-10"
              data-testid="pdf-viewer-pager"
            >
              <button
                onClick={(e) => { e.stopPropagation(); goRel(-1); }}
                disabled={pageIdx === 0}
                className="p-1 disabled:opacity-30"
                aria-label="Föregående sida"
                data-testid="pdf-viewer-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-xs font-medium tabular-nums" data-testid="pdf-viewer-page-counter">
                {pageIdx + 1} / {pages.length}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); goRel(1); }}
                disabled={pageIdx === pages.length - 1}
                className="p-1 disabled:opacity-30"
                aria-label="Nästa sida"
                data-testid="pdf-viewer-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
